/**
 * Upload limits and accepted file types enforced by `validateFile`.
 *
 * `maxSizeMB` / `maxSizeBytes` cap the per-file size at 50 MB.
 * `acceptedMimeTypes` and `acceptedExtensions` cover .xlsx, .xls, .docx, and .pdf.
 */
export const UPLOAD_CONSTRAINTS = {
  maxSizeMB: 50,
  maxSizeBytes: 50 * 1024 * 1024,
  acceptedMimeTypes: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/pdf', // .pdf
  ] as string[],
  acceptedExtensions: ['.xlsx', '.xls', '.docx', '.pdf'] as string[],
};

/**
 * Outcome of `validateFile`. `isValid` is `false` when the file fails an
 * extension or size check; in that case `error` carries a user-facing reason.
 */
export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Returns the lowercased extension of `fileName`, including the leading dot
 * (for example, `".xlsx"`). Returns an empty string when the name has no `.`
 * separator.
 */
export function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? `.${parts.pop()?.toLowerCase()}` : '';
}

/**
 * Formats a byte count as a human-readable string using binary units
 * (Bytes, KB, MB, GB) rounded to two decimal places. Returns `"0 Bytes"`
 * when `bytes` is zero.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Validates an uploaded `File` against `UPLOAD_CONSTRAINTS`.
 *
 * Checks the file extension first, then the byte size. A MIME type that is
 * absent or not in the accepted list logs a console warning but does not
 * fail validation, since some browsers report MIME types unreliably.
 *
 * Returns `{ isValid: true }` when both checks pass, otherwise
 * `{ isValid: false, error }` describing the first failure.
 */
export function validateFile(file: File): FileValidationResult {
  // Check file extension
  const extension = getFileExtension(file.name);
  if (!UPLOAD_CONSTRAINTS.acceptedExtensions.includes(extension)) {
    return {
      isValid: false,
      error: `Invalid file type "${extension}". Accepted formats: ${UPLOAD_CONSTRAINTS.acceptedExtensions.join(', ')}`,
    };
  }

  // Check file size
  if (file.size > UPLOAD_CONSTRAINTS.maxSizeBytes) {
    return {
      isValid: false,
      error: `File size (${formatFileSize(file.size)}) exceeds maximum allowed size of ${UPLOAD_CONSTRAINTS.maxSizeMB}MB`,
    };
  }

  // Check MIME type (with fallback for edge cases)
  if (file.type && !UPLOAD_CONSTRAINTS.acceptedMimeTypes.includes(file.type)) {
    // Some browsers may not correctly detect MIME type, so we rely on extension as primary check
    console.warn(`MIME type ${file.type} not in accepted list, but extension is valid`);
  }

  return { isValid: true };
}

/**
 * Returns `true` when the file's extension is `.xlsx` or `.xls`.
 */
export function isExcelFile(file: File): boolean {
  const extension = getFileExtension(file.name);
  return extension === '.xlsx' || extension === '.xls';
}

/**
 * Returns `true` when the file's extension is `.docx`.
 */
export function isWordFile(file: File): boolean {
  const extension = getFileExtension(file.name);
  return extension === '.docx';
}

/**
 * Returns `true` when the file's extension is `.pdf`.
 */
export function isPdfFile(file: File): boolean {
  const extension = getFileExtension(file.name);
  return extension === '.pdf';
}

